/**
 * The one HTTP client every `api/*` module goes through (constitution §II:
 * DRY — one client, not one `fetch` per feature). Attaches the session JWT,
 * mirrors the backend's structured error body (`api/app.py::_error_body`),
 * and always talks to same-origin `/api/*` (proxied to the backend in dev,
 * served by the same FastAPI process in prod).
 */

import { clearToken, getToken } from '../auth/session'
import type { ApiErrorBody } from '../types/domain'

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/**
 * `ApiErrorBody` as a JSON parse can actually hand it back -- a proxy error
 * page or a truncated response is not guaranteed to carry either field, so
 * this stays a defensive `Partial` rather than the wire type itself.
 */
type ErrorBody = Partial<{ error: Partial<ApiErrorBody['error']> }>

type UnauthorizedListener = () => void

const unauthorizedListeners = new Set<UnauthorizedListener>()

/**
 * Subscribe to "the server rejected our token" (`AuthContext` does, once).
 *
 * Every authenticated call funnels through here, so one place notices a 401 and
 * the app can end the session honestly. Without it a stale token — expired,
 * or minted against a different `SESSION_JWT_SECRET` — turns every screen into
 * a generic error while the UI still shows the user as signed in, with no way
 * out that doesn't involve devtools.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => unauthorizedListeners.delete(listener)
}

/**
 * Called by `client.ts` and `sse.ts` on any 401 from an authenticated request.
 *
 * Drops the dead token first, so nothing retries with it, then notifies. Signing
 * in is itself unauthenticated, so a rejected sign-in never reaches this.
 */
export function handleUnauthorized(): void {
  if (!getToken()) return
  clearToken()
  for (const listener of unauthorizedListeners) listener()
}

/**
 * Whether a rejection is "the caller cancelled", not "the request failed".
 *
 * A component that aborts on unmount, or a loader that supersedes its own
 * in-flight request, must not paint an error for it — the abort is the intended
 * outcome. Checked by name rather than instance: the browser throws a
 * `DOMException`, undici (used under vitest) throws its own error type.
 */
export function isAbortError(reason: unknown): boolean {
  return reason instanceof Error && reason.name === 'AbortError'
}

/** Reads the backend's structured error body (`api/app.py::_error_body`). */
export async function apiErrorFrom(res: Response): Promise<ApiError> {
  let message = res.statusText || `Request failed with status ${res.status}`
  try {
    const body = (await res.json()) as ErrorBody
    message = body.error?.message ?? message
  } catch {
    // Non-JSON error body (e.g. a proxy error page) — keep statusText.
  }
  if (res.status === 401) handleUnauthorized()
  return new ApiError(message, res.status)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`/api${path}`, { ...init, headers })

  if (!res.ok) throw await apiErrorFrom(res)

  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

function jsonBody(body: unknown): BodyInit | undefined {
  return body === undefined ? undefined : JSON.stringify(body)
}

/**
 * The default limit for a short request. Mutations use it through
 * `withMutationTimeout`, and CV upload recovery uses it for each status read.
 *
 * It is not applied by `httpClient` itself to every write: `POST
 * /apply/cv`, `/profile/refine` and onboarding's finalize step run a live
 * model call and can legitimately take longer than this. A call site opts
 * in with `withMutationTimeout`, the way `matches.ts`'s three mutations do,
 * rather than the client enforcing one blanket timeout that would "fix" the
 * inconsistency by cutting off those model calls too.
 */
export const REQUEST_TIMEOUT_MS = 10_000

/**
 * Adds one request timeout to an optional caller signal.
 *
 * This uses explicit listeners because `AbortSignal.any` is unavailable before
 * Safari 17.4. The request keeps the reason from the signal that ends it.
 */
export function withRequestTimeout(
  signal?: AbortSignal,
  timeoutMs = REQUEST_TIMEOUT_MS,
): AbortSignal {
  const timeout = AbortSignal.timeout(timeoutMs)
  if (!signal) return timeout

  const controller = new AbortController()
  const finish = (source: AbortSignal) => {
    signal.removeEventListener('abort', onCallerAbort)
    timeout.removeEventListener('abort', onTimeout)
    controller.abort(source.reason)
  }
  const onCallerAbort = () => finish(signal)
  const onTimeout = () => finish(timeout)

  if (signal.aborted) finish(signal)
  else if (timeout.aborted) finish(timeout)
  else {
    signal.addEventListener('abort', onCallerAbort, { once: true })
    timeout.addEventListener('abort', onTimeout, { once: true })
  }
  return controller.signal
}

/**
 * Combines a caller's own `AbortSignal` (component unmount, a superseding
 * request) with a hard mutation timeout, so either can end the request —
 * without this, a hung mutation leaves its button's pending state set
 * forever, since `setPending(null)` only ever runs in the caller's `.finally`.
 */
export function withMutationTimeout(signal?: AbortSignal): AbortSignal {
  return withRequestTimeout(signal)
}

/**
 * Fetches a non-JSON body (the apply artifact download is `text/markdown`,
 * not `application/json`, so `request`'s `JSON.parse` would break on it).
 * Still attaches the session token like every other call — the download
 * route requires auth the same as its siblings.
 */
async function requestText(path: string, signal?: AbortSignal): Promise<string> {
  const token = getToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`/api${path}`, { signal, headers })
  if (!res.ok) throw await apiErrorFrom(res)
  return res.text()
}

/**
 * The typed client every `api/*` module is built on.
 *
 * Each method takes an optional `AbortSignal` so a caller can cancel: a
 * component aborting on unmount, or a loader superseding its own earlier
 * request so a slow first response cannot land after a fast second one and
 * overwrite it. Rejections from an abort satisfy `isAbortError`.
 */
export const httpClient = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    request<T>(path, { method: 'POST', body: jsonBody(body), signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    request<T>(path, { method: 'PUT', body: jsonBody(body), signal }),
  upload: <T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> =>
    request<T>(path, { method: 'POST', body: form, signal }),
  getText: (path: string, signal?: AbortSignal): Promise<string> => requestText(path, signal),
  // The backend answers erasure with `204 No Content`, which `request` maps to
  // `undefined` — hence `Promise<void>` at every call site rather than a body type.
  delete: (path: string, signal?: AbortSignal): Promise<void> =>
    request<void>(path, { method: 'DELETE', signal }),
}
